import { useContext } from 'react';
import { WorkspaceConfigContext } from '@r/context/workspace-config-context.ts';

interface WorkspaceConfigContextType {
  groupedConfigs?: {
    style?: {
      primary?: string;
    };
  };
}

const useColorGradient = (numberOfObjectives: number): string[] => {
  const colors: string[] = [];

  const workspaceConfigContext = useContext(WorkspaceConfigContext) as WorkspaceConfigContextType;
  const primaryColorString = workspaceConfigContext?.groupedConfigs?.style?.primary;

  const defaultColor = 'hsl(240, 5.9%, 10%)';

  if (primaryColorString) {
    const hslMatch = primaryColorString.match(/(\d+)\s+(\d+)%\s+(\d+)%/);

    if (hslMatch?.[1] && hslMatch[2] && hslMatch[3]) {
      const primaryHue = Number.parseInt(hslMatch[1], 10);
      const primarySaturation = Number.parseInt(hslMatch[2], 10);
      const primaryLightness = Number.parseInt(hslMatch[3], 10);

      colors.push(`hsl(${primaryHue}, ${primarySaturation}%, ${primaryLightness}%)`);

      const numberOfLighterShades = numberOfObjectives - 1;

      const baseHue = primaryHue;
      const baseSaturation = primarySaturation;
      let currentLightness = primaryLightness;
      const endLightness = 95;

      if (numberOfLighterShades > 0 && endLightness > currentLightness) {
        const lightnessIncrement = (endLightness - currentLightness) / numberOfLighterShades;

        for (let i = 0; i < numberOfLighterShades; i++) {
          currentLightness += lightnessIncrement;
          colors.push(
            `hsl(${baseHue}, ${baseSaturation}%, ${Math.min(currentLightness, endLightness)}%)`
          );
        }
      } else if (numberOfLighterShades > 0) {
        let currentLightnessFallback = 70;
        const lightnessIncrementFallback =
          25 / (numberOfLighterShades > 0 ? numberOfLighterShades : 1);
        for (let i = 0; i < numberOfLighterShades; i++) {
          currentLightnessFallback = Math.min(
            currentLightnessFallback + lightnessIncrementFallback,
            95
          );
          colors.push(`hsl(0, 0%, ${currentLightnessFallback}%)`);
        }
      }
    } else {
      console.error('Could not parse HSL from primary color string:', primaryColorString);
      colors.push(defaultColor);
      const numberOfFallbackShades = numberOfObjectives - 1;
      if (numberOfFallbackShades > 0) {
        let currentLightnessFallback = 70;
        const lightnessIncrementFallback =
          25 / (numberOfFallbackShades > 0 ? numberOfFallbackShades : 1);
        for (let i = 0; i < numberOfFallbackShades; i++) {
          currentLightnessFallback = Math.min(
            currentLightnessFallback + lightnessIncrementFallback,
            95
          );
          colors.push(`hsl(0, 0%, ${currentLightnessFallback}%)`);
        }
      }
    }
  } else {
    colors.push(defaultColor);
    const numberOfFallbackShades = numberOfObjectives - 1;
    if (numberOfFallbackShades > 0) {
      let currentLightnessFallback = 70;
      const lightnessIncrementFallback =
        25 / (numberOfFallbackShades > 0 ? numberOfFallbackShades : 1);
      for (let i = 0; i < numberOfFallbackShades; i++) {
        currentLightnessFallback = Math.min(
          currentLightnessFallback + lightnessIncrementFallback,
          95
        );
        colors.push(`hsl(0, 0%, ${currentLightnessFallback}%)`);
      }
    }
  }

  return colors;
};

export { useColorGradient };
